import { inject } from '@angular/core'

import { DialogService } from 'primeng/dynamicdialog'
import { merge, take, takeUntil } from 'rxjs'

import { injectDestroy$ } from '@ti/core/common/utils'
import {
  BulkActionWithLoadingDialogData,
  ConfirmDialogData,
  ConfirmWithAgreeDialogData,
  ConfirmWithCheckboxResultType,
  ConfirmWithCommentDialogData,
  CopyDialogData,
  IDialogFacade,
  NotifyWithTemplateDialogData,
  WarnDialogData,
} from '@ti/core/contracts/dialog'

export class DialogFacade implements IDialogFacade {
  protected readonly dialogService = inject(DialogService)
  protected readonly destroy$ = injectDestroy$()

  public async confirm(data: ConfirmDialogData, duplicate = false): Promise<boolean> {
    const { ConfirmDialogComponent } =
      await import('../components/confirm-popup/confirm-dialog.component')
    return new Promise((resolve) => {
      const ref = this.dialogService.open(ConfirmDialogComponent, {
        data,
        showHeader: false,
        modal: true,
        styleClass: 'unset-default-style',
        duplicate: duplicate,
      })
      if (ref) {
        merge(ref.onClose, ref.onDestroy).pipe(take(1), takeUntil(this.destroy$)).subscribe(resolve)
      }
    })
  }

  public async confirmWithSubAction(
    data: ConfirmDialogData,
    componentType?: any,
  ): Promise<false | true | 'sub-action'> {
    if (!componentType) {
      const { ConfirmDialogComponent } =
        await import('../components/confirm-popup/confirm-dialog.component')
      componentType = ConfirmDialogComponent
    }
    return new Promise((resolve) => {
      const ref = this.dialogService.open(componentType, {
        data,
        showHeader: false,
        modal: true,
        styleClass: 'unset-default-style',
        // need to open several times
        // duplicate: true,
      })
      if (ref) {
        merge(ref.onClose, ref.onDestroy).pipe(take(1), takeUntil(this.destroy$)).subscribe(resolve)
      }
    })
  }

  public async notify(data: NotifyWithTemplateDialogData): Promise<boolean> {
    const { NotifyWithTemplateDialogComponent } =
      await import('../components/notify-dialog/notify-dialog.component')
    return new Promise((resolve) => {
      const ref = this.dialogService.open(NotifyWithTemplateDialogComponent, {
        data,
        showHeader: false,
        modal: true,
        styleClass: 'unset-default-style m-12 w340',
        // need to open several times
        // duplicate: true,
      })
      if (ref) {
        merge(ref.onClose, ref.onDestroy).pipe(take(1), takeUntil(this.destroy$)).subscribe(resolve)
      }
    })
  }

  public async copy(data: CopyDialogData): Promise<boolean> {
    const { CopyDialogComponent } = await import('../components/copy-dialog/copy-dialog.component')
    return new Promise((resolve) => {
      const ref = this.dialogService.open(CopyDialogComponent, {
        data,
        showHeader: false,
        modal: true,
        styleClass: 'unset-default-style',
        // need to open several times
        // duplicate: true,
      })
      if (ref) {
        merge(ref.onClose, ref.onDestroy).pipe(take(1), takeUntil(this.destroy$)).subscribe(resolve)
      }
    })
  }

  public async confirmWithComment(
    data: ConfirmWithCommentDialogData,
    componentType?: any,
  ): Promise<
    {
      result: boolean
      commentText: string
    } & Record<string, any>
  > {
    if (!componentType) {
      const { ConfirmWithCommentDialogComponent } =
        await import('../components/confirm-with-comment/confirm-with-comment-dialog.component')
      componentType = ConfirmWithCommentDialogComponent
    }
    return new Promise((resolve) => {
      const ref = this.dialogService.open(componentType, {
        data,
        showHeader: false,
        modal: true,
        styleClass: 'unset-default-style',
        // need to open several times
        // duplicate: true,
      })
      if (ref) {
        merge(ref.onClose, ref.onDestroy).pipe(take(1), takeUntil(this.destroy$)).subscribe(resolve)
      }
    })
  }

  public async leavePage(): Promise<boolean> {
    const { LeavePageDialogComponent } =
      await import('../components/leave-page-dialog/leave-page-dialog.component')
    return new Promise((resolve) => {
      const ref = this.dialogService.open(LeavePageDialogComponent, {
        showHeader: false,
        modal: true,
        styleClass: 'unset-default-style',
        // need to open several times
        // duplicate: true,
      })
      if (ref) {
        merge(ref.onClose, ref.onDestroy).pipe(take(1), takeUntil(this.destroy$)).subscribe(resolve)
      }
    })
  }

  public async bulkActionWithLoading(data: BulkActionWithLoadingDialogData): Promise<{
    result: boolean
    value: { all: number; success: number; fail: number }
  }> {
    const { BulkActionWithLoadingDialogComponent } =
      await import('../components/bulk-action-with-loading/bulk-action-with-loading-dialog.component')
    return new Promise((resolve) => {
      const ref = this.dialogService.open(BulkActionWithLoadingDialogComponent, {
        data,
        showHeader: false,
        modal: true,
        styleClass: 'unset-default-style',
        // need to open confirm several times
        // duplicate: true,
      })
      if (ref) {
        merge(ref.onClose, ref.onDestroy).pipe(take(1), takeUntil(this.destroy$)).subscribe(resolve)
      }
    })
  }

  public async warn(data: WarnDialogData) {
    const { WarnDialogComponent } = await import('../components/warn-popup/warn-dialog.component')
    return new Promise((resolve) => {
      const ref = this.dialogService.open(WarnDialogComponent, {
        data,
        showHeader: false,
        modal: true,
        styleClass: 'unset-default-style',
        // need to open confirm several times
        duplicate: true,
      })
      if (ref) {
        merge(ref.onClose, ref.onDestroy).pipe(take(1), takeUntil(this.destroy$)).subscribe(resolve)
      }
    })
  }

  public async confirmWithAgree(
    data: ConfirmWithAgreeDialogData,
  ): Promise<ConfirmWithCheckboxResultType> {
    const { ConfirmWithAgreeDialogComponent } =
      await import('../components/confirm-with-agree/confirm-with-agree-dialog.component')
    return new Promise((resolve) => {
      const ref = this.dialogService.open(ConfirmWithAgreeDialogComponent, {
        data,
        showHeader: false,
        modal: true,
        styleClass: 'unset-default-style',
        // need to open confirm several times
        duplicate: true,
      })
      if (ref) {
        merge(ref.onClose, ref.onDestroy).pipe(take(1), takeUntil(this.destroy$)).subscribe(resolve)
      }
    })
  }
}
